import {
  ENERGY_COST, PRICES, SimState, TRUCK_ARRIVAL_INTERVAL,
  TRUCK_LOAD_WINDOW, TRUCK_TRAVEL_TIME, TRUCK_CARGO_CAP, TRUCK_PLATFORM_CAP, XP_REWARDS,
  CargoKind, CARGO_KINDS, emptyCargo,
} from './types';
import { EnergySystem } from './EnergySystem';
import { ProgressionSystem } from './ProgressionSystem';
import { EventBus } from './events';

/**
 * The shipping truck.
 *
 *   idle ── nextArrival ──▶ loading ── load window ──▶ departed
 *                                                     ──── travel ──▶ returning
 *                                                                       ──── travel ──▶ idle (with coins)
 *
 * The player loads crops/wood/foraged items into truck.cargo while it's
 * `loading`. Pressing "Dispatch" departs early. When it returns, coins are
 * deposited.
 */

/** How much of `kind` does the player currently have in their inventory? */
function readRes(state: SimState, kind: CargoKind): number {
  const r = state.res;
  switch (kind) {
    case 'wheat':     return r.basket;
    case 'corn':      return r.corn;
    case 'carrot':    return r.carrot;
    case 'flour':     return r.flour;
    case 'cornFlour': return r.cornFlour ?? 0;
    case 'wood':      return r.wood;
    case 'mushroom':  return r.mushrooms ?? 0;
    case 'berry':     return r.berries ?? 0;
    case 'acorn':     return r.treeSeeds ?? 0;
  }
}

/** Add `delta` to the inventory of `kind`. Clamped at 0 on the low side so
 *  bugs that try to over-decrement can't push counts negative. */
function adjustRes(state: SimState, kind: CargoKind, delta: number) {
  const r = state.res;
  switch (kind) {
    case 'wheat':     r.basket = Math.max(0, r.basket + delta); break;
    case 'corn':      r.corn   = Math.max(0, r.corn + delta); break;
    case 'carrot':    r.carrot = Math.max(0, r.carrot + delta); break;
    case 'flour':     r.flour  = Math.max(0, r.flour + delta); break;
    case 'cornFlour': r.cornFlour = Math.max(0, (r.cornFlour ?? 0) + delta); break;
    case 'wood':      r.wood   = Math.max(0, r.wood + delta); break;
    case 'mushroom':  r.mushrooms = Math.max(0, (r.mushrooms ?? 0) + delta); break;
    case 'berry':     r.berries   = Math.max(0, (r.berries ?? 0) + delta); break;
    case 'acorn':     r.treeSeeds = Math.max(0, (r.treeSeeds ?? 0) + delta); break;
  }
}

/** XP awarded per item sold of `kind`. Used by dispatch. */
function sellXp(kind: CargoKind): number {
  switch (kind) {
    case 'wheat':     return XP_REWARDS.sellWheat;
    case 'corn':      return XP_REWARDS.sellCorn;
    case 'carrot':    return XP_REWARDS.sellCarrot;
    case 'flour':     return XP_REWARDS.sellFlour;
    case 'cornFlour': return XP_REWARDS.sellFlour;     // premium grind — same as flour
    case 'wood':      return XP_REWARDS.sellWood;
    case 'mushroom':  return XP_REWARDS.sellCorn;       // foraged delicacy
    case 'berry':     return XP_REWARDS.sellWheat;
    case 'acorn':     return XP_REWARDS.sellWheat;
  }
}

export const TruckSystem = {
  /** Advance the truck state machine. The truck is always loadable +
   *  dispatchable whenever it's parked (idle OR loading) — there's no
   *  "wait for next arrival" friction. `idle` just means "parked,
   *  player hasn't touched it yet"; `loading` means "player loaded
   *  something, optional auto-dispatch countdown is ticking." Player
   *  can dispatch from either state at any time. */
  tick(state: SimState, dt: number, bus: EventBus) {
    const t = state.truck;
    if (t.status === 'idle') {
      // Truck waits indefinitely. Platform items are NOT auto-drained
      // into the cargo bed — that was confusing players who'd load via
      // the panel, see the truck visibly fill, and think the previous
      // trip's money was a glitch. Drain only happens at dispatch.
      return;
    }
    if (t.status === 'loading') {
      // Truck no longer auto-dispatches. Player must explicitly press
      // "Send truck to city" to start the trip. We keep the loading
      // status as a brief visual nudge (panel says "Loading at depot")
      // but never decrement the timer to zero — relax to idle on the
      // next tick so the panel doesn't pretend a countdown is happening.
      t.status = 'idle';
      return;
    }
    if (t.status === 'departed') {
      t.timer -= dt;
      if (t.timer <= 0) {
        // Truck arrived at the city — goods are sold here, not at
        // dispatch. Emit the unload event BEFORE clearing cargo so a
        // listener (Diorama) can read items/coins and play a delivery
        // flash matching what was on the bed.
        const items = this.cargoTotal(t);
        bus.emit({ type: 'truckUnloadedAtCity', items, coins: t.returnPayload });
        t.cargo = emptyCargo();
        t.status = 'returning';
        t.timer = TRUCK_TRAVEL_TIME;
      }
      return;
    }
    if (t.status === 'returning') {
      t.timer -= dt;
      if (t.timer <= 0) {
        const paid = t.returnPayload;
        const items = t.returnItems ?? 0;
        state.res.money += paid;
        bus.emit({ type: 'truckReturned', coins: paid });
        bus.emit({ type: 'sold', coins: paid, items });
        t.returnPayload = 0;
        t.returnItems = 0;
        // Deliver any seed purchases the player queued at dispatch time.
        // The truck went to town and bought them — they land in the
        // player's seed inventory now, not at click time.
        if (Array.isArray(t.pendingSeeds) && t.pendingSeeds.length > 0) {
          let totalSeeds = 0;
          for (const o of t.pendingSeeds) {
            state.res.seeds[o.seed] = (state.res.seeds[o.seed] ?? 0) + o.qty;
            totalSeeds += o.qty;
            bus.emit({ type: 'seedsDelivered', seed: o.seed, qty: o.qty });
          }
          t.pendingSeeds = [];
          bus.emit({ type: 'hint',
            text: `Truck back with ${totalSeeds} seed${totalSeeds === 1 ? '' : 's'} from the city.` });
        }
        // Deliver any stone the player ordered. Same flow as seeds — the
        // truck went to town with the player's money, and the stone lands
        // in inventory on the return leg.
        if (t.pendingStone && t.pendingStone.qty > 0) {
          state.res.stone = (state.res.stone ?? 0) + t.pendingStone.qty;
          bus.emit({ type: 'hint', text: `Truck back with ${t.pendingStone.qty} stone.` });
          t.pendingStone = undefined;
        }
        // Return to parked state immediately — no arrival countdown.
        t.status = 'idle';
        t.nextArrival = 0;
        // Pull anything the player queued onto the depot platform while
        // the truck was away onto the cargo bed now — they wanted those
        // items on the truck, and the truck is finally here.
        if (this.platformTotal(t) > 0) this.drainPlatformToCargo(t);
        // Heads-up that the truck is back so the TripBar / HUD can
        // refresh without a per-frame hash diff.
        bus.emit({ type: 'truckArrived' });
      }
      return;
    }
  },

  /** Total items currently riding on the truck. */
  cargoTotal(t: SimState['truck']): number {
    let n = 0;
    for (const k of CARGO_KINDS) n += t.cargo[k] ?? 0;
    return n;
  },
  /** Total items waiting on the depot platform. */
  platformTotal(t: SimState['truck']): number {
    const p = t.platform; if (!p) return 0;
    let n = 0;
    for (const k of CARGO_KINDS) n += p[k] ?? 0;
    return n;
  },

  /** Move platform items onto the truck, capped at TRUCK_CARGO_CAP. Anything
   *  that doesn't fit stays on the platform. Returns the number of items
   *  actually moved so callers can phrase a hint correctly. */
  drainPlatformToCargo(t: SimState['truck']): number {
    const plat = t.platform;
    if (!plat) return 0;
    let room = TRUCK_CARGO_CAP - this.cargoTotal(t);
    if (room <= 0) return 0;
    let moved = 0;
    for (const k of CARGO_KINDS) {
      if (room <= 0) break;
      const take = Math.min(plat[k] ?? 0, room);
      if (take <= 0) continue;
      plat[k] -= take;
      t.cargo[k] += take;
      room -= take;
      moved += take;
    }
    return moved;
  },

  /** Truck is loadable + dispatchable any time it's parked (idle OR
   *  loading). The first load from idle flips the state into loading
   *  and starts the auto-dispatch countdown so the player can fire
   *  cargo and walk away if they want — but they can also click
   *  Dispatch immediately. */
  isParked(t: SimState['truck']): boolean {
    return t.status === 'idle' || t.status === 'loading';
  },
  /** Legacy no-op kept for call-site compatibility. The truck used to
   *  promote idle → loading here so an auto-dispatch timer could fire;
   *  auto-dispatch was removed (player must explicitly press Send) so
   *  there's no longer any reason to flip status on load. */
  ensureLoading(_t: SimState['truck']) {
    return;
  },

  /** Load one unit of `kind` from the player's storage into the truck. */
  load(state: SimState, kind: CargoKind, bus: EventBus): boolean {
    const t = state.truck;
    if (!this.isParked(t)) {
      bus.emit({ type: 'blocked', reason: 'Truck has left — wait for it to return.' });
      return false;
    }
    if (this.cargoTotal(t) >= TRUCK_CARGO_CAP) {
      bus.emit({ type: 'blocked', reason: `Truck is full (${TRUCK_CARGO_CAP} items max).` });
      return false;
    }
    if (readRes(state, kind) <= 0) {
      bus.emit({ type: 'blocked', reason: `No ${kind} to load.` });
      return false;
    }
    this.ensureLoading(t);
    adjustRes(state, kind, -1);
    t.cargo[kind]++;
    EnergySystem.spend(state, ENERGY_COST.loadTruck);
    bus.emit({ type: 'truckLoaded', kind, qty: 1 });
    return true;
  },

  /** Load ALL of one kind, up to the cap AND the player's energy budget.
   *  Each item costs ENERGY_COST.loadTruck to match the single-item path —
   *  bulk loading is convenient, not free. */
  loadAll(state: SimState, kind: CargoKind, bus: EventBus): number {
    const t = state.truck;
    if (!this.isParked(t)) { bus.emit({ type: 'blocked', reason: 'Truck has left — wait for it to return.' }); return 0; }
    const room = TRUCK_CARGO_CAP - this.cargoTotal(t);
    if (room <= 0) {
      bus.emit({ type: 'blocked', reason: `Truck is full (${TRUCK_CARGO_CAP} items max).` });
      return 0;
    }
    const available = readRes(state, kind);
    const energyBudget = ENERGY_COST.loadTruck > 0
      ? Math.floor(state.res.energy / ENERGY_COST.loadTruck)
      : Infinity;
    const n = Math.min(available, room, energyBudget);
    if (n === 0) {
      if (available > 0 && room > 0 && energyBudget === 0) {
        bus.emit({ type: 'blocked', reason: 'Not enough energy to load.' });
      }
      return 0;
    }
    this.ensureLoading(t);
    adjustRes(state, kind, -n);
    t.cargo[kind] += n;
    state.res.energy = Math.max(0, state.res.energy - n * ENERGY_COST.loadTruck);
    bus.emit({ type: 'truckLoaded', kind, qty: n });
    return n;
  },

  /** Move one unit of `kind` from storage onto the depot platform. Works
   *  at any time — truck doesn't need to be present. */
  sendToPlatform(state: SimState, kind: CargoKind, bus: EventBus): boolean {
    const plat = state.truck.platform;
    if (!plat) return false;
    if (this.platformTotal(state.truck) >= TRUCK_PLATFORM_CAP) {
      bus.emit({ type: 'blocked', reason: `Platform is full (${TRUCK_PLATFORM_CAP} items max). Wait for the truck to pick up.` });
      return false;
    }
    if (readRes(state, kind) <= 0) {
      bus.emit({ type: 'blocked', reason: `No ${kind} to send.` });
      return false;
    }
    // Energy parity with loadTruck — loading via the platform shouldn't
    // be free when loading directly costs energy. Same fee.
    if (!EnergySystem.spend(state, ENERGY_COST.loadTruck)) {
      bus.emit({ type: 'blocked', reason: 'Not enough energy to load.' });
      return false;
    }
    adjustRes(state, kind, -1);
    plat[kind]++;
    bus.emit({ type: 'platformChanged' });
    // The platform is just a staging area before the truck picks it up,
    // but to the player it reads as "loading the truck". Emit truckLoaded
    // so FlyToHud animates the item from the storage chip down to the
    // truck's screen position.
    bus.emit({ type: 'truckLoaded', kind, qty: 1 });
    return true;
  },

  /** Move as many units of `kind` from storage onto the platform as fit. */
  sendAllToPlatform(state: SimState, kind: CargoKind, bus: EventBus): number {
    const plat = state.truck.platform;
    if (!plat) return 0;
    const room = TRUCK_PLATFORM_CAP - this.platformTotal(state.truck);
    if (room <= 0) {
      bus.emit({ type: 'blocked', reason: `Platform is full (${TRUCK_PLATFORM_CAP} items max).` });
      return 0;
    }
    const avail = readRes(state, kind);
    // Energy budget — can't load more items than the player has stamina
    // for. Match loadAll's behaviour.
    const energyBudget = ENERGY_COST.loadTruck > 0
      ? Math.floor(state.res.energy / ENERGY_COST.loadTruck)
      : Infinity;
    const n = Math.min(avail, room, energyBudget);
    if (n === 0) {
      if (avail > 0 && room > 0 && energyBudget === 0) {
        bus.emit({ type: 'blocked', reason: 'Not enough energy to load.' });
      }
      return 0;
    }
    adjustRes(state, kind, -n);
    plat[kind] += n;
    state.res.energy = Math.max(0, state.res.energy - n * ENERGY_COST.loadTruck);
    bus.emit({ type: 'platformChanged' });
    bus.emit({ type: 'truckLoaded', kind, qty: n });
    return n;
  },

  /** Pull one unit of `kind` back off the platform into storage. */
  recallFromPlatform(state: SimState, kind: CargoKind, bus: EventBus): boolean {
    const plat = state.truck.platform;
    if (!plat) return false;
    if ((plat[kind] ?? 0) <= 0) return false;
    plat[kind]--;
    adjustRes(state, kind, +1);
    bus.emit({ type: 'platformChanged' });
    return true;
  },

  /** Player dispatches the truck (or auto when load window expires). */
  dispatch(state: SimState, bus: EventBus, auto = false): boolean {
    const t = state.truck;
    // Player can dispatch any time the truck is parked (idle OR loading).
    // Only block when the truck has already left.
    if (!this.isParked(t)) {
      if (!auto) {
        const reason =
          t.status === 'departed'  ? 'Truck has already left for the city.' :
          t.status === 'returning' ? 'Truck is on its way back.' :
                                      'Truck isn\'t available right now.';
        bus.emit({ type: 'blocked', reason });
      }
      return false;
    }
    // Drag idle into loading momentarily so the downstream code path
    // (drainPlatformToCargo, cargo math) sees a consistent state.
    this.ensureLoading(t);
    this.drainPlatformToCargo(t);
    // Compute coins + XP across every cargo kind so foraged items count.
    let coins = 0;
    let xp = 0;
    for (const k of CARGO_KINDS) {
      const n = t.cargo[k] ?? 0;
      if (n <= 0) continue;
      coins += n * PRICES[k];
      xp    += n * sellXp(k);
    }
    const items = this.cargoTotal(t);
    const hasPendingSeeds = Array.isArray(t.pendingSeeds) && t.pendingSeeds.length > 0;
    const hasPendingStone = !!(t.pendingStone && t.pendingStone.qty > 0);
    // Truck can depart with empty cargo IF it has seed/stone purchases
    // queued — the round-trip is required to fetch those from the city.
    // Otherwise an empty-cargo manual dispatch is rejected.
    if (items === 0 && !hasPendingSeeds && !hasPendingStone && !auto) {
      bus.emit({ type: 'hint', text: 'Empty truck — load something first.' });
      return false;
    }
    if (xp > 0) ProgressionSystem.addXp(state, xp, bus);
    t.returnPayload = coins;
    t.returnItems = items;
    // DON'T clear cargo here — the crates stay visible on the bed for
    // the outbound trip. The cargo is wiped only when the truck arrives
    // at the city (departed → returning transition above), which reads
    // as "unloaded at the market".
    t.status = 'departed';
    t.timer = TRUCK_TRAVEL_TIME;
    bus.emit({ type: 'truckDeparted', coins });
    return true;
  },
};
